import { ReactElement, useState, useEffect } from 'react';
import { post } from 'utils/network';
import { ApiRoute, Route } from 'config';
import { toCurrency } from 'utils/numbers';
import Plan, { PlanName, PlanType, Programs } from 'models/Plan';
import useCurrentUser from 'hooks/useCurrentUser';
import Modal from 'components/shared/Modal';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import MealKitIcon from 'icons/icon-circle--meal-kit.svg';
import RteIcon from 'icons/icon-circle--ready-to-eat.svg';
import DiversifiedIcon from 'icons/icon-circle--mix-and-match.svg';

import SelectableImageTile from '../shared/SelectableImageTile';
import DropdownWithIcon from '../shared/DropdownWithIcon';
import SelectableServingQuantity from '../SignUp/SelectPlan/SelectableServingQuantity';
import SelectableExtrasQuantity from '../SignUp/SelectPlan/SelectableExtrasQuantity';
import ItemizedList from '../shared/ItemizedList';
import {
  DEFAULT_MEAL_KIT_EXTRAS,
  DEFAULT_PREPARED_EXTRAS,
  DEFAULT_NO_EXTRAS,
  DEFAULT_SERVINGS,
  LIST_ITEMS_BY_PLAN_TYPE,
  PREFERENCES,
  PREFERENCES_BY_PLAN_TYPE,
} from './utils/constants';

type Props = {
  show: boolean;
  existingPlan: Plan;
  onClose: () => void;
  showSubscriptionCancelTrigger?: boolean;
  onSubscriptionCancel?: () => void;
};

function UpdatePlan({
  show,
  existingPlan,
  onClose,
  showSubscriptionCancelTrigger = false,
  onSubscriptionCancel,
}: Props): ReactElement {
  const [selectedPlan, setSelectedPlan] = useState(existingPlan);
  const [selectedMealKitServings, setSelectedMealKitServings] = useState(DEFAULT_SERVINGS);
  const [selectedMealKitExtras, setSelectedMealKitExtras] = useState(DEFAULT_MEAL_KIT_EXTRAS);
  const [selectedPreparedExtras, setSelectedPreparedExtras] = useState(DEFAULT_PREPARED_EXTRAS);

  const { reload: reloadUser } = useCurrentUser();
  const showServingsToggle = ['chefs_choice', 'four_servings'].includes(selectedPlan.name) && selectedPlan.type !== PlanType.DIVERSIFIED;
  const showExtrasToggle = [PlanType.MEAL_KIT, PlanType.PREPARED].includes(selectedPlan.type);

  const handlePlanChange = (planName: PlanName, isDiversified = false) => {
    let newPlan: Plan;
    switch (planName) {
      case 'jumpstart_program':
        newPlan = Plan.getPlan(1, DEFAULT_NO_EXTRAS, Programs.JUMPSTART_PROGRAM);
        break;
      case 'prepared_one_serving':
        newPlan = Plan.getPlan(1, selectedPreparedExtras);
        break;
      case 'four_servings':
        setSelectedMealKitServings(4);
        newPlan = Plan.getPlan(4, selectedMealKitExtras, planName);
        break;
      default:
        setSelectedMealKitServings(2);
        newPlan = Plan.getPlan(2, selectedMealKitExtras, planName, isDiversified);
    }

    setSelectedPlan(newPlan);
  };

  // only "chef's choice" gives the option of 2 vs. 4 servings (4 servings = 'four-servings' plan)
  const handleMealKitServingChange = (servings: number) => {
    let newPlan = selectedPlan;
    if (servings === 4) {
      newPlan = Plan.getPlan(servings, selectedMealKitExtras);
    } else if (servings === 2) {
      newPlan = Plan.getPlan(servings, selectedMealKitExtras, 'chefs_choice');
    }

    setSelectedMealKitServings(servings);
    setSelectedPlan(newPlan);
  };

  const handleExtrasChange = (extraDinners: number) => {
    const selectedExtras = {
      dinner: extraDinners,
      breakfast: 0,
      lunch: 0,
    };
    const newPlan = Plan.getPlan(selectedPlan.servings, selectedExtras, selectedPlan.name);
    if (newPlan.type === PlanType.MEAL_KIT) {
      setSelectedMealKitExtras(selectedExtras);
    } else {
      setSelectedPreparedExtras(selectedExtras);
    }

    setSelectedPlan(newPlan);
  };

  const handleSubmit = async () => {
    if (showSubscriptionCancelTrigger) {
      window.location.href = Route.PROFILE;
    }

    await post(`${ApiRoute.USERS}/me/subscription`, {
      subscription: {
        plan: selectedPlan.name,
        extra_meals: selectedPlan.isDiversified ? DEFAULT_NO_EXTRAS : selectedPlan.selectedExtras,
        is_diversified: selectedPlan.isDiversified,
      },
    });

    reloadUser();
  };

  useEffect(() => {
    setSelectedPlan(existingPlan);

    if (existingPlan.type === PlanType.MEAL_KIT) {
      setSelectedMealKitServings(existingPlan.servings);
      setSelectedMealKitExtras(existingPlan.selectedExtras);
    } else if (existingPlan.type === PlanType.PREPARED) {
      setSelectedPreparedExtras(existingPlan.selectedExtras);
    }
  }, [existingPlan]);

  return (
    <Modal
      show={show}
      className="c-update-plan-modal"
      body={(
        <>
          <button
            className="close c-update-plan-modal__close"
            type="button"
            aria-label="Close modal"
            onClick={onClose}
          >
            <CloseIcon width="32" height="32" />
          </button>

          <div className="c-update-plan">
            <h2 className="c-heading--brand-4">Update Plan</h2>

            <div className="c-update-plan__plans-container">
              <SelectableImageTile
                label="Meal Kit"
                value={PlanType.MEAL_KIT}
                isSelected={selectedPlan.type === PlanType.MEAL_KIT}
                imageUrl={MealKitIcon}
                onClick={() => selectedPlan.type !== PlanType.MEAL_KIT
                  && handlePlanChange('chefs_choice', false)}
              />
              <SelectableImageTile
                label="Ready-to-Eat"
                value={PlanType.PREPARED}
                isSelected={[PlanType.PREPARED, PlanType.PROGRAMS].includes(selectedPlan.type)}
                imageUrl={RteIcon}
                onClick={() => ![PlanType.PREPARED, PlanType.PROGRAMS].includes(selectedPlan.type)
                  && handlePlanChange('prepared_one_serving', false)}
              />
              <SelectableImageTile
                label="Mix & Match"
                value={PlanType.DIVERSIFIED}
                isSelected={selectedPlan.type === PlanType.DIVERSIFIED}
                imageUrl={DiversifiedIcon}
                onClick={() => selectedPlan.type !== PlanType.DIVERSIFIED
                  && handlePlanChange('chefs_choice', true)}
              />
            </div>

            <DropdownWithIcon
              selectedOption={PREFERENCES[selectedPlan.name]}
              options={PREFERENCES_BY_PLAN_TYPE[selectedPlan.type]}
              title={[PlanType.PREPARED, PlanType.PROGRAMS].includes(selectedPlan.type) ? 'Program' : 'Dietary Preference'}
              onSelect={(preference) => handlePlanChange(
                preference.value, selectedPlan.type === PlanType.DIVERSIFIED,
              )}
            />

            <div className="c-update-plan__servings-container">
              {showServingsToggle ? (
                <>
                  <hr />
                  <SelectableServingQuantity
                    planType={selectedPlan.type}
                    selectedServings={selectedMealKitServings}
                    handleSelectServings={handleMealKitServingChange}
                    planName={selectedPlan.name}
                  />
                </>
              ) : (
                <ItemizedList
                  list={LIST_ITEMS_BY_PLAN_TYPE[selectedPlan.type]}
                  planName={selectedPlan.name}
                />
              )}

              {showExtrasToggle && (
                <>
                  <hr />
                  <SelectableExtrasQuantity
                    plan={selectedPlan}
                    handleSelectExtras={handleExtrasChange}
                    showMostPopularWidget={false}
                  />
                  <hr />
                </>
              )}

              {!showServingsToggle && !showExtrasToggle && (<hr />)}
            </div>

            <div className="c-update-plan__price">
              <span className="u-text--md">Weekly Price</span>
              <span className="u-text--md">{toCurrency(selectedPlan.planPrice)}</span>
            </div>

            <button
              type="button"
              className="c-button--primary"
              onClick={() => {
                handleSubmit();
                onClose();
                window.location.reload();
              }}
            >
              Select This Plan
            </button>
          </div>

          {showSubscriptionCancelTrigger && onSubscriptionCancel && (
            <button
              type="button"
              className="c-link c-update-plan__cancel-plan"
              onClick={onSubscriptionCancel}
              style={{
                padding: 0,
                border: 0,
              }}
              data-cy="modal-cancel-subscription"
            >
              No thanks, I&apos;d like to cancel
            </button>
          )}
        </>
      )}
    />
  );
}

export default UpdatePlan;
